import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { AuthCardLayout } from "@/components/layout/AuthCardLayout";
import { Button } from "@/components/ui/button";
import { activateCustomer } from "@/lib/api/auth";
import { useSession } from "@/lib/session/SessionContext";

/**
 * Kein Zwischenschritt mehr: der Link aus der Willkommensmail aktiviert das
 * Konto automatisch und leitet direkt zu /onboarding/welcome weiter — die
 * frühere "Konto aktivieren"-Schaltfläche entfällt. startedRef verhindert,
 * dass der Effekt (z. B. durch React StrictMode im Dev-Modus) den
 * Einmal-Token doppelt einlöst.
 */
export function RegisterPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const { applyBootstrap } = useSession();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token || startedRef.current) return;
    startedRef.current = true;
    activateCustomer(token)
      .then((result) => {
        applyBootstrap(result);
        navigate("/onboarding/willkommen", { replace: true });
      })
      .catch((activationError) => {
        setError(activationError instanceof Error ? activationError.message : "Die Aktivierung ist fehlgeschlagen.");
      });
  }, [token, applyBootstrap, navigate]);

  return (
    <AuthCardLayout>
      {!token ? (
        <>
          <h3 className="mb-2 flex items-center justify-center gap-2 text-lg font-bold text-primary">
            <AlertTriangle className="size-5" aria-hidden="true" /> Kein gültiger Aktivierungslink
          </h3>
          <p className="mb-6 text-sm leading-[1.45] text-muted-foreground">
            Dieser Link ist unvollständig oder abgelaufen. Fordere bei Bedarf eine neue Einladung an.
          </p>
        </>
      ) : error ? (
        <>
          <h3 className="mb-2 flex items-center justify-center gap-2 text-lg font-bold text-primary">
            <AlertTriangle className="size-5" aria-hidden="true" /> Aktivierung fehlgeschlagen
          </h3>
          <p className="mb-6 text-sm leading-[1.45] text-muted-foreground">{error}</p>
        </>
      ) : (
        <>
          <h3 className="mb-2 flex items-center justify-center gap-2 text-lg font-bold text-primary">
            <Loader2 className="size-5 animate-spin" aria-hidden="true" /> Dein Onboarding wird vorbereitet ...
          </h3>
          <p className="mb-6 text-sm leading-[1.45] text-muted-foreground">Du wirst gleich automatisch weitergeleitet.</p>
        </>
      )}

      <div className="mt-6 border-t border-dashed border-border pt-4">
        <Button type="button" variant="outline" className="w-full text-primary" onClick={() => navigate("/login")}>
          <ArrowLeft className="size-3.5" aria-hidden="true" /> Zum Login
        </Button>
      </div>
    </AuthCardLayout>
  );
}
